'use client';

import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';

import { useEffect } from 'react';

const BRIDGE_URL = 'https://cribl.io/fullstory-cross-domain/bridge.html';
const BRIDGE_ORIGIN = new URL(BRIDGE_URL).origin;
const FS_UID_COOKIE = 'fs_uid';
const FS_READINESS_POLL_MS = 200;
const FS_READINESS_TIMEOUT_MS = 10_000;

declare global {
  interface Window {
    FS?: {
      event?: (name: string, properties?: Record<string, unknown>) => void;
      identify?: (uid: string | false, vars?: Record<string, unknown>) => void;
      setUserVars?: (vars: Record<string, string | boolean>) => void;
      shutdown?: () => void;
      restart?: () => void;
    };
  }
}

function getFullStoryUid(): string | null {
  const match = document.cookie.match(new RegExp(FS_UID_COOKIE + '=([^;#]+)'));

  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function waitForFullStory(callback: (fs: NonNullable<Window['FS']>) => void): () => void {
  if (window.FS?.setUserVars) {
    callback(window.FS);

    return () => {};
  }

  const interval = setInterval(() => {
    if (window.FS?.setUserVars) {
      clearInterval(interval);
      clearTimeout(timeout);
      callback(window.FS);
    }
  }, FS_READINESS_POLL_MS);

  const timeout = setTimeout(() => clearInterval(interval), FS_READINESS_TIMEOUT_MS);

  return () => {
    clearInterval(interval);
    clearTimeout(timeout);
  };
}

/**
 * Syncs FullStory UIDs across cribl.cloud and cribl.io via a hidden iframe
 * bridge. Sends the local fs_uid to cribl.io for storage and retrieves any
 * previously stored UID to link sessions in FullStory.
 */
export const FullStoryCrossDomainSync: React.FC = () => {
  const { cloudFullstoryCrossDomainSession } = useFlagsProxy();

  useEffect(() => {
    if (!cloudFullstoryCrossDomainSession) {
      return;
    }

    const currentFsUid = getFullStoryUid();

    const bridgeIframe = document.createElement('iframe');
    bridgeIframe.style.display = 'none';
    bridgeIframe.src = BRIDGE_URL;

    let cleanupFsWait = () => {};

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== BRIDGE_ORIGIN) {
        return;
      }

      if (event.data?.type !== 'FS_SYNC_RESPONSE') {
        return;
      }

      if (event.data.action === 'READY') {
        if (currentFsUid) {
          bridgeIframe.contentWindow?.postMessage(
            { type: 'FS_SYNC', action: 'STORE', fs_uid: currentFsUid },
            BRIDGE_ORIGIN,
          );
        }
        bridgeIframe.contentWindow?.postMessage(
          { type: 'FS_SYNC', action: 'RETRIEVE' },
          BRIDGE_ORIGIN,
        );
      }

      if (event.data.action === 'RETRIEVED' && event.data.fs_uid) {
        cleanupFsWait = waitForFullStory((fs) => {
          fs.setUserVars?.({
            crossDomainUid_str: event.data.fs_uid,
            crossDomainSource_str: 'cribl.io',
            crossDomainLinked_bool: true,
          });
        });
      }
    };

    window.addEventListener('message', handleMessage);
    document.body.appendChild(bridgeIframe);

    return () => {
      window.removeEventListener('message', handleMessage);
      bridgeIframe.remove();
      cleanupFsWait();
    };
  }, []);

  return null;
};
